import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, List, Carousel, Statistic, Button, Space, Tag } from 'antd';
import { BookOutlined, CalendarOutlined, InfoCircleOutlined, UserOutlined, TrophyOutlined, StarOutlined, ClockCircleOutlined, RiseOutlined } from '@ant-design/icons';
import './Home.less';

const { Title, Paragraph, Text } = Typography;

// 轮播图数据
const carouselItems = [
  { image: 'https://picsum.photos/1200/400?random=1', title: '欢迎来到羊小羊大学', description: '培养未来领袖的摇篮' },
  { image: 'https://picsum.photos/1200/400?random=2', title: '2024年招生季', description: '期待你的加入' },
  { image: 'https://picsum.photos/1200/400?random=3', title: '校园开放日', description: '走进校园，感受魅力' },
  { image: 'https://picsum.photos/1200/400?random=4', title: '科研成果展', description: '探索未知，追求真理' }
];

// 新闻公告数据
const newsItems = [
  { id: 1, title: '2024年秋季学期开学通知', date: '2024-08-15', type: '通知' },
  { id: 2, title: '我校在全国大学生数学竞赛中取得优异成绩', date: '2024-08-10', type: '喜报' },
  { id: 3, title: '关于开展2024年教师节表彰活动的通知', date: '2024-08-05', type: '通知' },
  { id: 4, title: '我校新增3个国家级一流本科专业建设点', date: '2024-07-30', type: '新闻' },
  { id: 5, title: '2024年暑期社会实践活动圆满结束', date: '2024-07-25', type: '新闻' },
  { id: 6, title: '2024级新生报到须知', date: '2024-08-20', type: '通知' }
];

// 特色专业数据
const featuredMajors = [
  { name: '计算机科学与技术', level: '国家级一流专业', students: '1200+', teachers: '85' },
  { name: '电子信息工程', level: '国家级一流专业', students: '1000+', teachers: '72' },
  { name: '机械工程', level: '省级一流专业', students: '950+', teachers: '68' },
  { name: '工商管理', level: '省级一流专业', students: '850+', teachers: '60' }
];

// 师资力量数据
const facultyStats = [
  { title: '教职工总数', value: '1200+', icon: <UserOutlined />, color: '#3f8600' },
  { title: '教授', value: '280+', icon: <StarOutlined />, color: '#1890ff' },
  { title: '副教授', value: '450+', icon: <StarOutlined />, color: '#722ed1' },
  { title: '博士学位教师', value: '850+', icon: <BookOutlined />, color: '#eb2f96' }
];

// 校园动态数据
const campusActivities = [
  { title: '校园文化节', date: '2024-09-20', location: '田径场', participants: '5000+' },
  { title: '新生开学典礼', date: '2024-09-01', location: '体育馆', participants: '3000+' },
  { title: '创新创业大赛', date: '2024-08-28', location: '学术报告厅', participants: '200+' },
  { title: '校园足球联赛', date: '2024-08-15', location: '足球场', participants: '300+' }
];

const Home: React.FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 模拟数据加载
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="home-container">
      {/* 轮播图 */}
      <Carousel autoplay className="home-carousel">
        {carouselItems.map((item, index) => (
          <div key={index} className="carousel-item">
            <img src={item.image} alt={item.title} className="carousel-image" />
            <div className="carousel-overlay">
              <Title level={2} className="carousel-title">{item.title}</Title>
              <Paragraph className="carousel-description">{item.description}</Paragraph>
              <Button type="primary" size="large">了解更多</Button>
            </div>
          </div>
        ))}
      </Carousel>

      {/* 新闻公告与校园动态 */}
      <div className="news-campus-row">
        {/* 新闻公告 */}
        <div className="news-column">
          <Card className="home-section" title={<Title level={3}><RiseOutlined /> 新闻公告</Title>}>
            <List
              loading={loading}
              dataSource={newsItems}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Tag color={item.type === '通知' ? 'blue' : item.type === '喜报' ? 'green' : 'orange'}>{item.type}</Tag>,
                    <Text type="secondary"><ClockCircleOutlined /> {item.date}</Text>
                  ]}
                >
                  <List.Item.Meta
                    title={<a href={`#news-${item.id}`}>{item.title}</a>}
                  />
                </List.Item>
              )}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="default">查看更多</Button>
            </div>
          </Card>
        </div>

        {/* 校园动态 */}
        <div className="campus-column">
          <Card className="home-section" title={<Title level={3}><CalendarOutlined /> 校园动态</Title>}>
            <List
              loading={loading}
              dataSource={campusActivities}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={<a href={`#activity-${item.title}`}>{item.title}</a>}
                    description={
                      <Space>
                        <Text type="secondary"><ClockCircleOutlined /> {item.date}</Text>
                        <Text type="secondary"><UserOutlined /> {item.participants}</Text>
                        <Text type="secondary">{item.location}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="default">查看更多</Button>
            </div>
          </Card>
        </div>
      </div>

      {/* 特色专业 */}
      <Card className="home-section" title={<Title level={3}><BookOutlined /> 特色专业</Title>}>
        <Row gutter={[16, 16]}>
          {featuredMajors.map((major, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card hoverable className="major-card">
                <Title level={4}>{major.name}</Title>
                <Tag color={major.level.includes('国家级') ? 'green' : 'blue'}>{major.level}</Tag>
                <div className="major-stats">
                  <Space direction="vertical" size="small" style={{ marginTop: 12 }}>
                    <div><UserOutlined /> 学生人数：{major.students}</div>
                    <div><StarOutlined /> 教师人数：{major.teachers}</div>
                  </Space>
                </div>
                <Button type="primary" size="small" style={{ marginTop: 16 }}>
                  了解详情
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* 办学成果 */}
      <Card className="home-section" title={<Title level={3}><TrophyOutlined /> 办学成果</Title>}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="国家级一流专业"
                value={8}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
              <Paragraph style={{ marginTop: 16 }}>
                学校现有8个国家级一流本科专业建设点，15个省级一流本科专业建设点。
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="科研项目"
                value="1500+"
                prefix={<BookOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
              <Paragraph style={{ marginTop: 16 }}>
                近年来，学校承担各类科研项目1500余项，其中国家级项目200余项。
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="毕业生就业率"
                value="96.2%"
                prefix={<UserOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
              <Paragraph style={{ marginTop: 16 }}>
                学校毕业生就业率连续五年保持在95%以上，就业质量稳步提升。
              </Paragraph>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 学校简介 */}
      <Card className="home-section" title={<Title level={3}><InfoCircleOutlined /> 学校简介</Title>}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={16}>
            <Paragraph>
              羊小羊大学是一所历史悠久、学科齐全、师资力量雄厚、具有鲜明特色的综合性大学。学校始建于1952年，占地面积2000余亩，建筑面积80余万平方米。
            </Paragraph>
            <Paragraph>
              学校现有20个学院，涵盖工、理、管、文、法、经、教育、艺术等8大学科门类，开设60余个本科专业，拥有10个一级学科博士点，25个一级学科硕士点。
            </Paragraph>
            <Paragraph>
              学校坚持以学生为中心，以质量为生命线，致力于培养具有创新精神和实践能力的高素质人才。
            </Paragraph>
            <Button type="default">查看详情</Button>
          </Col>
          <Col xs={24} md={8}>
            <Row gutter={[16, 16]}>
              {facultyStats.map((stat, index) => (
                <Col xs={12} key={index}>
                  <Card>
                    <Statistic
                      title={stat.title}
                      value={stat.value}
                      prefix={stat.icon}
                      valueStyle={{ color: stat.color }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Home;